import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box, Card, CardContent, Typography, Chip, Alert,
  CircularProgress, Button, Skeleton, Tooltip, IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Calculate,
  Shield, Info as InfoIcon,
  ShowChart, Warning, CheckCircle, TrendingUp, TrendingDown,
  FileDownloadOutlined,
} from '@mui/icons-material';
import {
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, ReferenceLine, Line, ComposedChart, Legend,
} from 'recharts';
import html2canvas from 'html2canvas';
import { sentimentAPI, behaviorStatsAPI, psychologyAPI } from '../services/api';
import {
  SimilarDaysVisualization, EventPerformanceTable, VNIndexCorrelationChart,
  BehaviorOverview,
} from '../components/BehaviorInsights';
import { getRelevantEventNames } from '../components/BehaviorInsights/eventPatternUtils';
import { MarketPsychologySection } from '../components/MarketPsychology';

/* ─── delta badge (shows change vs previous day) ─── */
const DeltaBadge = ({ current, previous, suffix = '', invert = false, decimals = 1 }) => {
  if (current == null || previous == null) return null;
  const delta = current - previous;
  if (Math.abs(delta) < 0.001) return null;
  // invert: for fear/stress, going UP is bad (red), going DOWN is good (green)
  const isPositive = invert ? delta < 0 : delta > 0;
  const Icon = delta > 0 ? TrendingUp : TrendingDown;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3,
      bgcolor: isPositive ? 'rgba(76,175,80,0.25)' : 'rgba(244,67,54,0.25)',
      borderRadius: 1, px: 0.7, py: 0.15, ml: 0.8 }}>
      <Icon sx={{ fontSize: '0.7rem', color: 'white' }} />
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'white' }}>
        {delta > 0 ? '+' : ''}{delta.toFixed(decimals)}{suffix}
      </Typography>
    </Box>
  );
};
/* ═══════════════════════════════════════════════════════════════
   Row 1 — 4 hero stat cards
   Backend (signal_classifier.py) returns semantic state codes in
   `data.classification`. Frontend just maps codes → i18n + colors.
   No business logic here.
   ═══════════════════════════════════════════════════════════════ */

const K = (k) => `sentiment.heroCards.${k}`;

// --- code → i18n key tables -----------------------------------------------
const MSI_LABELS = {
  panic:                { big: 'msiPanic',      sub: 'msiPanicSub' },
  bottoming:            { big: 'msiBottoming',  sub: 'msiBottomingSub' },
  recovery:             { big: 'msiRecovery',   sub: 'msiRecoverySub' },
  cautious:             { big: 'msiCautious',   sub: 'msiCautiousSub' },
  neutral:              { big: 'msiNeutral',    sub: 'msiNeutralSub' },
  divergence_neutral:   { big: 'msiDivergence', sub: 'msiDivergenceNeutralSub' },
  positive:             { big: 'msiPositive',   sub: 'msiPositiveSub' },
  divergence_positive:  { big: 'msiDivergence', sub: 'msiDivergenceSub' },
  euphoria:             { big: 'msiEuphoria',   sub: 'msiEuphoriaSub' },
  overheat:             { big: 'msiOverheat',   sub: 'msiOverheatSub' },
};

const FEAR_LABELS = {
  extreme_greed:        { big: 'fearExtremeGreed',       sub: 'fearExtremeGreedSub' },
  optimistic:           { big: 'fearOptimistic',         sub: 'fearOptimisticSub' },
  slightly_optimistic:  { big: 'fearSlightlyOptimistic', sub: 'fearSlightlyOptimisticSub' },
  normal:               { big: 'fearNormal',             sub: 'fearNormalSub' },
  worried:              { big: 'fearWorried',            sub: 'fearWorriedSub' },
  high:                 { big: 'fearHigh',               sub: 'fearHighSub' },
  very_high:            { big: 'fearVeryHigh',           sub: 'fearVeryHighSub' },
};

const STRESS_LABELS = {
  calm:     { big: 'stressCalm',     sub: 'stressCalmSub' },
  stable:   { big: 'stressStable',   sub: 'stressStableSub' },
  moderate: { big: 'stressModerate', sub: 'stressModerateSub' },
  tense:    { big: 'stressTense',    sub: 'stressTenseSub' },
  danger:   { big: 'stressDanger',   sub: 'stressDangerSub' },
};
const HIDDEN_RISK_SUBS = {
  breadth_divergence: 'stressHiddenBreadthSub',
  distribution:       'stressHiddenDistSub',
  euphoria:           'stressHiddenEuphoriaSub',
};
// stress_context reconciles today's stress reading with the multi-day trend
// (e.g. confirmed bottoming can still show high same-day stress).
const STRESS_CONTEXT_NOTES = {
  easing_confirmed: 'stressContextEasingConfirmed',
};

// action_code → {big_key, sub_key, withCash?: true}
const ACTION_LABELS = {
  euphoria_trim:      { big: 'expEuphoriaTrim',     sub: 'expEuphoriaTrimSub',     withCash: true  },
  euphoria_hold:      { big: 'expEuphoriaHold',     sub: 'expEuphoriaHoldSub'                      },
  euphoria_wait:      { big: 'expEuphoriaWait',     sub: 'expEuphoriaWaitSub'                      },
  panic_cut:          { big: 'expPanicCut',         sub: 'expPanicCutSub',         withCash: true  },
  panic_reduce:       { big: 'expPanicReduce',      sub: 'expPanicReduceSub',      withCash: true  },
  panic_shelter:      { big: 'expPanicShelter',     sub: 'expPanicShelterSub'                      },
  bottom_scale:       { big: 'expBottomScale',      sub: 'expBottomScaleSub',      withCash: true  },
  bottom_add:         { big: 'expBottomAdd',        sub: 'expBottomAddSub'                         },
  bottom_wait:        { big: 'expBottomWait',       sub: 'expBottomWaitSub'                        },
  dist_trim:          { big: 'expDistTrim',         sub: 'expDistTrimSub',         withCash: true  },
  dist_reduce:        { big: 'expDistReduce',       sub: 'expDistReduceSub',       withCash: true  },
  dist_avoid:         { big: 'expDistAvoid',        sub: 'expDistAvoidSub'                         },
  stress_reduce:      { big: 'expStressReduce',     sub: 'expStressReduceSub',     withCash: true  },
  stress_cautious:    { big: 'expStressCautious',   sub: 'expStressCautiousSub',   withCash: true  },
  narrow_trim_heavy:  { big: 'expNarrowTrimHeavy',  sub: 'expNarrowTrimHeavySub',  withCash: true  },
  narrow_trim:        { big: 'expNarrowTrim',       sub: 'expNarrowTrimSub',       withCash: true  },
  buy_selective:      { big: 'expBuySelective',     sub: 'expBuySelectiveSub',     withCash: true  },
  buy_strong:         { big: 'expBuyStrong',        sub: 'expBuyStrongSub'                         },
  buy_active:         { big: 'expBuyActive',        sub: 'expBuyActiveSub'                         },
  buy_gradual:        { big: 'expBuyGradual',       sub: 'expBuyGradualSub',       withCash: true  },
  buy_kickstart:      { big: 'expBuyKickstart',     sub: 'expBuyKickstartSub',     withCash: true  },
  defensive:          { big: 'expDefensive',        sub: 'expDefensiveSub',        withCash: true  },
  sidelined:          { big: 'expSidelined',        sub: 'expSidelinedSub'                         },
};

// --- gradient palette (frontend-only concern) -----------------------------
const G = {
  panic:        'linear-gradient(135deg, #b71c1c 0%, #d32f2f 100%)',
  danger:       'linear-gradient(135deg, #c62828 0%, #e53935 100%)',
  warning:      'linear-gradient(135deg, #e65100 0%, #f57c00 100%)',
  divergence:   'linear-gradient(135deg, #ef6c00 0%, #fb8c00 100%)',
  bottoming:    'linear-gradient(135deg, #0277bd 0%, #29b6f6 100%)',
  recovery:     'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)',
  euphoria:     'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)',
  healthy:      'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
  fear:         'linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%)',
  neutral:      'linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)',
  cautious:     'linear-gradient(135deg, #f57f17 0%, #fbc02d 100%)',
};

const MSI_GRADIENT = {
  panic: G.panic, bottoming: G.warning, recovery: G.recovery, cautious: G.cautious,
  neutral: G.cautious, divergence_neutral: G.divergence,
  positive: G.healthy, divergence_positive: G.divergence,
  euphoria: G.warning, overheat: G.euphoria,
};
const STRESS_GRADIENT = {
  calm: G.neutral, stable: G.neutral, moderate: G.neutral,
  tense: G.warning, danger: G.danger, hidden: G.warning,
};
const ACTION_GRADIENT = {
  euphoria_trim: G.euphoria, euphoria_hold: G.euphoria, euphoria_wait: G.euphoria,
  panic_cut: G.panic, panic_reduce: G.panic, panic_shelter: G.panic,
  bottom_scale: G.bottoming, bottom_add: G.bottoming, bottom_wait: G.cautious,
  dist_trim: G.warning, dist_reduce: G.warning, dist_avoid: G.warning,
  stress_reduce: G.danger, stress_cautious: G.warning,
  narrow_trim_heavy: G.warning, narrow_trim: G.warning,
  buy_selective: G.warning, defensive: G.warning, sidelined: G.warning,
  buy_strong: G.healthy, buy_active: G.healthy,
  buy_gradual: G.healthy, buy_kickstart: G.healthy,
};

// --- lookup helper --------------------------------------------------------
const lookupLabel = (t, table, code, params) => {
  const entry = table[code];
  if (!entry) return { big: code || '—', sub: '' };
  return {
    big: t(K(entry.big)),
    sub: params ? t(K(entry.sub), params) : t(K(entry.sub)),
  };
};

/* ─── Card 1: MSI ─── */
const MSIScoreCard = ({ data, prev, t }) => {
  if (!data) return null;
  const c = data.classification || {};
  const msi = data.msi ?? 50;
  const lbl = lookupLabel(t, MSI_LABELS, c.msi_zone);
  const cardGradient = MSI_GRADIENT[c.msi_zone] || G.cautious;

  return (
    <Card sx={{ height: '100%', width: '100%', background: cardGradient }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', fontWeight: 600, mb: 0.5 }}>
            {t('sentiment.heroCards.marketSentiment')}
          </Typography>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.2, mb: 0.5 }}>
            {lbl.big}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            {lbl.sub}
          </Typography>
        </Box>
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.25)', mb: 0.5 }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${msi}%`, bgcolor: 'white', borderRadius: 3 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.fear')}</Typography>
            <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
              MSI: {msi.toFixed(1)}/100
              <DeltaBadge current={msi} previous={prev?.msi} />
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.greed')}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ─── Card 2: Fear ─── */
const FearScoreCard = ({ data, prev, t }) => {
  if (!data) return null;
  const c = data.classification || {};
  const fear = data.fear_score ?? 0;
  const pct = Math.min(Math.max((fear + 2) / 4 * 100, 0), 100);
  const lbl = lookupLabel(t, FEAR_LABELS, c.fear_zone);

  return (
    <Card sx={{ height: '100%', width: '100%', background: G.fear }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', fontWeight: 600, mb: 0.5 }}>
            {t('sentiment.heroCards.fearLevel')}
          </Typography>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.2, mb: 0.5 }}>
            {lbl.big}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            {lbl.sub}
          </Typography>
        </Box>
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.25)', mb: 0.5 }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, bgcolor: 'white', borderRadius: 3 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.greed')}</Typography>
            <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
              {fear.toFixed(3)}
              <DeltaBadge current={fear} previous={prev?.fear_score} decimals={3} invert />
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.fear')}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ─── Card 3: Stress ─── */
const StressCard = ({ data, prev, t }) => {
  if (!data) return null;
  const c = data.classification || {};
  const pct = c.stress_adjusted ?? (data.stress_score ?? 0) * 100;

  // Hidden risk zone uses a special "Rủi Ro Ẩn" big + variant sub
  const lbl = c.stress_zone === 'hidden'
    ? {
        big: t(K('stressHidden')),
        sub: t(K(HIDDEN_RISK_SUBS[c.hidden_risk] || 'stressHiddenBreadthSub')),
      }
    : lookupLabel(t, STRESS_LABELS, c.stress_zone);
  const cardBg = STRESS_GRADIENT[c.stress_zone] || G.neutral;
  const contextNoteKey = STRESS_CONTEXT_NOTES[c.stress_context];

  return (
    <Card sx={{ height: '100%', width: '100%', background: cardBg }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', fontWeight: 600, mb: 0.5 }}>
            {t('sentiment.heroCards.stressLevel')}
          </Typography>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.2, mb: 0.5 }}>
            {lbl.big}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            {lbl.sub}
          </Typography>
          {contextNoteKey && (
            <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.68rem', fontWeight: 600, mt: 0.5 }}>
              {t(K(contextNoteKey))}
            </Typography>
          )}
        </Box>
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.25)', mb: 0.5 }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, bgcolor: 'white', borderRadius: 3 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.calm')}</Typography>
            <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
              {pct.toFixed(1)}%
              <DeltaBadge current={pct} previous={prev?.stress_score != null ? prev.stress_score * 100 : null} suffix="%" invert />
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.danger')}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ─── Card 4: Action ─── */
const ExposureCard = ({ data, prev, t }) => {
  if (!data) return null;
  const c = data.classification || {};
  // Backend's recommended_exposure is regime-aware (capped by action_code).
  // Raw `exposure_target` is shown only as a small footnote so user can see
  // the engine's unconstrained sizing if they want.
  const rawExposure = (data.exposure_target ?? 0) * 100;
  const exposure = c.recommended_exposure ?? rawExposure;
  const hedge = (data.hedge_ratio ?? 0) * 100;
  const cash = Math.max(0, 100 - exposure);

  const code = c.action_code || 'sidelined';
  const entry = ACTION_LABELS[code] || ACTION_LABELS.sidelined;
  const lbl = lookupLabel(
    t, ACTION_LABELS, code,
    entry.withCash ? { cash: cash.toFixed(0) } : undefined,
  );
  const cardBg = ACTION_GRADIENT[code] || G.neutral;

  return (
    <Card sx={{ height: '100%', width: '100%', background: cardBg }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', fontWeight: 600, mb: 0.5 }}>
            {t('sentiment.heroCards.whatToDo')}
          </Typography>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.2, mb: 0.5 }}>
            {lbl.big}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.72rem', lineHeight: 1.4 }}>
            {lbl.sub}
          </Typography>
        </Box>
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.25)', mb: 0.5 }}>
            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${exposure}%`, bgcolor: 'white', borderRadius: 3 }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.cash')}</Typography>
            <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>
              {t('sentiment.heroCards.stock')} {exposure.toFixed(0)}% · Hedge {hedge.toFixed(0)}%
              <DeltaBadge current={exposure} previous={prev?.classification?.recommended_exposure ?? (prev?.exposure_target != null ? prev.exposure_target * 100 : null)} suffix="%" />
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem' }}>{t('sentiment.heroCards.allIn')}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Trader Plan Strip — alert / urgency / conviction / hedge / invalidation
   Reads `data.classification.{alert_level, urgency, conviction,
   hedge_recommended, invalidation: {exit, reenter}}`.
   ═══════════════════════════════════════════════════════════════ */

const ALERT_COLOR = {
  calm:   { bg: '#e8f5e9', fg: '#2e7d32', dot: '#43a047' },
  watch:  { bg: '#fff8e1', fg: '#ef6c00', dot: '#fb8c00' },
  action: { bg: '#fff3e0', fg: '#d84315', dot: '#e65100' },
  urgent: { bg: '#fce4ec', fg: '#b71c1c', dot: '#d32f2f' },
};

const URGENCY_COLOR = {
  immediate: '#d32f2f',
  fast:      '#e65100',
  staged:    '#1976d2',
  patient:   '#558b2f',
};

const camelize = (snake) =>
  snake.split('_').map((p, i) => i === 0 ? p : p[0].toUpperCase() + p.slice(1)).join('');

const CONVICTION_TIERS = [
  { min: 75, key: 'convStrong',   color: '#2e7d32', bar: '#43a047' },
  { min: 55, key: 'convModerate', color: '#ef6c00', bar: '#fb8c00' },
  { min: 35, key: 'convWeak',     color: '#757575', bar: '#9e9e9e' },
  { min: 0,  key: 'convVeryWeak', color: '#9e9e9e', bar: '#bdbdbd' },
];

const getConvictionTier = (conviction) => CONVICTION_TIERS.find((tier) => conviction >= tier.min);

const Cell = ({ label, children, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, ...sx }}>
    <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

Cell.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  sx: PropTypes.object,
};

Cell.defaultProps = {
  sx: undefined,
};

const TraderPlanStrip = ({ data, t }) => {
  if (!data?.classification) return null;
  const c = data.classification;
  const alert = c.alert_level || 'calm';
  const urgency = c.urgency || 'patient';
  const conviction = c.conviction != null ? c.conviction : null;
  const hedge = c.hedge_recommended != null ? c.hedge_recommended : null;
  const inv = c.invalidation || {};
  const T = (k) => t(`sentiment.traderPlan.${k}`);
  const alertC = ALERT_COLOR[alert] || ALERT_COLOR.calm;

  // exit_when codes → i18n key (camelCase: exit + PascalCase code)
  const exitKey = inv.exit ? `exit${inv.exit[0].toUpperCase()}${camelize(inv.exit).slice(1)}` : null;
  const reenterKey = inv.reenter ? `reenter${inv.reenter[0].toUpperCase()}${camelize(inv.reenter).slice(1)}` : null;

  return (
    <Card sx={{ mb: 2, bgcolor: alertC.bg, border: `1px solid ${alertC.dot}33` }}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: '1.1fr 1fr 1fr 1fr 1.5fr 1.5fr' }, gap: 2, alignItems: 'center' }}>
          {/* Alert level */}
          <Cell label={T('alert')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: alertC.dot }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: alertC.fg }}>
                {T(`alert${alert[0].toUpperCase()}${alert.slice(1)}`)}
              </Typography>
            </Box>
          </Cell>
          {/* Urgency */}
          <Cell label={T('urgency')}>
            <Tooltip title={T(`urgency${urgency[0].toUpperCase()}${urgency.slice(1)}Hint`)} arrow>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: URGENCY_COLOR[urgency] || 'text.primary', cursor: 'help' }}>
                {T(`urgency${urgency[0].toUpperCase()}${urgency.slice(1)}`)}
              </Typography>
            </Tooltip>
          </Cell>
          {/* Conviction */}
          <Cell label={T('conviction')}>
            {conviction != null ? (() => {
              const tier = getConvictionTier(conviction);
              return (
                <Tooltip title={T('convictionHint')} arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'help' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: tier.color, lineHeight: 1 }}>
                        {T(tier.key)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                        {conviction}/100
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, height: 4, bgcolor: 'rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'hidden', maxWidth: 60 }}>
                      <Box sx={{ width: `${conviction}%`, height: '100%', bgcolor: tier.bar }} />
                    </Box>
                  </Box>
                </Tooltip>
              );
            })() : <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>—</Typography>}
          </Cell>
          {/* Hedge */}
          <Cell label={T('hedge')}>
            <Tooltip title={T('hedgeHint')} arrow>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: hedge >= 10 ? '#d84315' : 'text.primary', cursor: 'help' }}>
                {hedge != null ? `${hedge.toFixed(0)}%` : '—'}
              </Typography>
            </Tooltip>
          </Cell>
          {/* Exit when */}
          <Cell label={T('exitWhen')}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: 'text.primary', lineHeight: 1.3 }}>
              {exitKey ? T(exitKey) : '—'}
            </Typography>
          </Cell>
          {/* Re-enter when */}
          <Cell label={T('reenterWhen')}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: 'text.primary', lineHeight: 1.3 }}>
              {reenterKey ? T(reenterKey) : '—'}
            </Typography>
          </Cell>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Row 2 — Fear Components + Positioning
   ═══════════════════════════════════════════════════════════════ */

const FearComponentsCard = ({ data, t }) => {
  if (!data) return null;
  const items = [
    { label: t('sentiment.fear.price'), w: 28, val: data.price_fear ?? 0, clr: '#e53935', hint: t('sentiment.fear.hintPrice') },
    { label: t('sentiment.fear.vol'), w: 22, val: data.vol_fear ?? 0, clr: '#fb8c00', hint: t('sentiment.fear.hintVol') },
    { label: t('sentiment.fear.volBeh'), w: 18, val: data.vol_beh_fear ?? 0, clr: '#fbc02d', hint: t('sentiment.fear.hintVolBeh') },
    { label: t('sentiment.fear.breadth'), w: 22, val: data.breadth_fear ?? 0, clr: '#43a047', hint: t('sentiment.fear.hintBreadth') },
    { label: t('sentiment.fear.deriv'), w: 10, val: data.deriv_fear ?? 0, clr: '#1e88e5', hint: t('sentiment.fear.hintDeriv') },
  ];
  const maxAbs = Math.max(...items.map(i => Math.abs(i.val)), 0.5);

  const getBarLabel = (val) => {
    if (val > 1.0) return t('sentiment.fear.levelVeryHigh');
    if (val > 0.4) return t('sentiment.fear.levelHigh');
    if (val > 0) return t('sentiment.fear.levelMild');
    if (val > -0.4) return t('sentiment.fear.levelGood');
    return t('sentiment.fear.levelVeryGood');
  };

  return (
    <Card sx={{ width: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            {t('sentiment.fear.title')}
          </Typography>
        </Box>

        {/* Rows */}
        {items.map((c, i) => {
          const pct = (Math.abs(c.val) / maxAbs) * 50;
          const pos = c.val >= 0;
          return (
            <Box key={c.label} sx={{
              px: 2, py: 1.25,
              borderBottom: i < items.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
            }}>
              {/* Row 1: label + score */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{c.label}</Typography>
                  <Chip label={`${c.w}%`} size="small" sx={{ height: 16, fontSize: '0.58rem', bgcolor: 'grey.100' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: pos ? '#e53935' : '#43a047' }}>
                    {getBarLabel(c.val)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, fontFamily: 'monospace', color: pos ? '#e53935' : '#43a047' }}>
                    {c.val.toFixed(3)}
                  </Typography>
                </Box>
              </Box>
              {/* Row 2: bar + hint */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, height: 10, bgcolor: 'grey.100', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                  <Box sx={{
                    position: 'absolute', top: 0, bottom: 0,
                    ...(pos ? { left: '50%', width: `${pct}%` } : { right: '50%', width: `${pct}%` }),
                    bgcolor: c.clr, opacity: 0.8, borderRadius: 1,
                  }} />
                  <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1.5, bgcolor: 'grey.300' }} />
                </Box>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {c.hint}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderTop: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Typography sx={{ fontSize: '0.88rem', fontWeight: 700 }}>{t('sentiment.fear.total')}</Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: 'primary.main' }}>
            {(data.fear_score ?? 0).toFixed(3)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const PositioningPanel = ({ data, t }) => {
  if (!data) return null;
  // Use regime-aware values from classification, falling back to raw engine
  // output. Keeps Positioning panel consistent with the hero ExposureCard.
  const c = data.classification || {};
  const exposurePct = c.recommended_exposure ?? (data.exposure_target ?? 0) * 100;
  const hedgePct    = c.hedge_recommended    ?? (data.hedge_ratio    ?? 0) * 100;
  const items = [
    { label: t('sentiment.positioning.exposure'), val: exposurePct, icon: <ShowChart sx={{ fontSize: 18 }} />, clr: '#1976d2', hint: t('sentiment.positioning.hintExposure') },
    { label: t('sentiment.positioning.hedge'),    val: hedgePct,    icon: <Shield sx={{ fontSize: 18 }} />,    clr: '#ff6d00', hint: t('sentiment.positioning.hintHedge') },
    { label: t('sentiment.positioning.risk'),     val: (data.risk_per_trade ?? 0) * 100, icon: <Warning sx={{ fontSize: 18 }} />, clr: '#e53935', hint: t('sentiment.positioning.hintRisk') },
    { label: t('sentiment.positioning.stop'),     val: (data.stop_pct ?? 0) * 100,       icon: <CheckCircle sx={{ fontSize: 18 }} />, clr: '#2e7d32', hint: t('sentiment.positioning.hintStop') },
  ];

  // Parse action_notes: "State=..., MSI=..., Stress=... | key1 | key2"
  const rawNotes = data.action_notes || '';
  const noteKeys = rawNotes.includes('|')
    ? rawNotes.split('|').slice(1).map(k => k.trim()).filter(Boolean)
    : [];
  const cleanNotes = noteKeys
    .map(k => t(`sentiment.positionNotes.${k}`, { defaultValue: '' }))
    .filter(Boolean)
    .join(' ');

  return (
    <Card sx={{ width: '100%', height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            {t('sentiment.positioning.title')}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, flex: 1 }}>
          {items.map((m, i) => (
            <Box key={m.label} sx={{
              px: 2, py: 1.25, display: 'flex', flexDirection: 'column', justifyContent: 'center',
              borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none',
              borderRight: i % 2 === 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                <Box sx={{ color: m.clr, display: 'flex' }}>{m.icon}</Box>
                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>{m.label}</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: m.clr, fontFamily: 'monospace' }}>
                {m.val.toFixed(1)}%
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled', mt: 0.25 }}>{m.hint}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.015)' }}>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.6 }}
            dangerouslySetInnerHTML={{ __html: t('sentiment.positioning.positionCalc', {
              exposure: exposurePct.toFixed(1),
              maxPos: (exposurePct / 5).toFixed(1),
              interpolation: { escapeValue: false },
            }) }}
          />
        </Box>

        {cleanNotes && (
          <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#e3f2fd', mt: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <InfoIcon sx={{ fontSize: 16, color: '#1565c0', mt: 0.25 }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#1565c0', lineHeight: 1.5, fontWeight: 500 }}>
                {cleanNotes}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Row 3 — Market Data table (like MarketHealthCard)
   ═══════════════════════════════════════════════════════════════ */

const MarketDataTable = ({ data, t }) => {
  if (!data) return null;

  const getTrend = (val, threshold, reverse = false) => {
    const bad = reverse ? val > threshold : val < threshold;
    if (bad) return { icon: <TrendingDown sx={{ fontSize: 16 }} />, color: '#e53935' };
    return { icon: <TrendingUp sx={{ fontSize: 16 }} />, color: '#43a047' };
  };

  const rows = [
    { metric: t('sentiment.indicators.dist20'), value: `${((data.dist20 ?? 0) * 100).toFixed(2)}%`, ...getTrend((data.dist20 ?? 0) * 100, 0), type: 'indicator', ref: t('sentiment.indicators.refDist20') },
    { metric: t('sentiment.indicators.dist50'), value: `${((data.dist50 ?? 0) * 100).toFixed(2)}%`, ...getTrend((data.dist50 ?? 0) * 100, 0), type: 'indicator', ref: t('sentiment.indicators.refDist50') },
    { metric: t('sentiment.indicators.dd20'), value: `${((data.dd20 ?? 0) * 100).toFixed(2)}%`, ...getTrend((data.dd20 ?? 0) * 100, -5), type: 'indicator', ref: t('sentiment.indicators.refDd20') },
    { metric: t('sentiment.indicators.dd60'), value: `${((data.dd60 ?? 0) * 100).toFixed(2)}%`, ...getTrend((data.dd60 ?? 0) * 100, -10), type: 'indicator', ref: t('sentiment.indicators.refDd60') },
    { metric: t('sentiment.indicators.atr'), value: `${((data.atr_pct ?? 0) * 100).toFixed(2)}%`, ...getTrend((data.atr_pct ?? 0) * 100, 2, true), type: 'indicator', ref: t('sentiment.indicators.refAtr') },
    { metric: t('sentiment.indicators.volShock'), value: (data.vol_shock ?? 0).toFixed(2), ...getTrend(data.vol_shock ?? 0, 1.5, true), type: 'indicator', ref: t('sentiment.indicators.refVolShock') },
    { metric: t('sentiment.breadth.above20'), value: `${((data.pct_above20 ?? 0) * 100).toFixed(1)}%`, ...getTrend((data.pct_above20 ?? 0) * 100, 50), type: 'breadth', ref: t('sentiment.breadth.refAbove20') },
    { metric: t('sentiment.breadth.above50'), value: `${((data.pct_above50 ?? 0) * 100).toFixed(1)}%`, ...getTrend((data.pct_above50 ?? 0) * 100, 40), type: 'breadth', ref: t('sentiment.breadth.refAbove50') },
    { metric: t('sentiment.breadth.adRatio'), value: (data.ad_ratio ?? 0).toFixed(2), ...getTrend(data.ad_ratio ?? 0, 1), type: 'breadth', ref: t('sentiment.breadth.refAdRatio') },
    { metric: t('sentiment.breadth.udVol'), value: (data.ud_vol_ratio ?? 0).toFixed(2), ...getTrend(data.ud_vol_ratio ?? 0, 1), type: 'breadth', ref: t('sentiment.breadth.refUdVol') },
    { metric: t('sentiment.breadth.nhNl'), value: (data.nh_nl ?? 0).toFixed(2), ...getTrend(data.nh_nl ?? 0, 0), type: 'breadth', ref: t('sentiment.breadth.refNhNl') },
  ];

  return (
    <Card sx={{ width: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShowChart sx={{ fontSize: '1.2rem' }} />
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            {t('sentiment.indicators.title')} & {t('sentiment.breadth.title')}
          </Typography>
        </Box>

        {/* Two-column data */}
        <Box sx={{ display: 'flex' }}>
          {/* Left column — Indicators */}
          <Box sx={{ flex: 1, borderRight: '1px solid rgba(0,0,0,0.06)' }}>
            <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                {t('sentiment.indicators.title')}
              </Typography>
            </Box>
            {rows.filter(r => r.type === 'indicator').map((row, idx, arr) => (
              <Box key={row.metric} sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                px: 2, py: 1,
                borderBottom: idx < arr.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
              }}>
                <Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{row.metric}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: 0.2 }}>{row.ref}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, fontFamily: 'monospace', color: row.color }}>
                    {row.value}
                  </Typography>
                  <Box sx={{ color: row.color, display: 'flex' }}>{row.icon}</Box>
                </Box>
              </Box>
            ))}
          </Box>
          {/* Right column — Breadth */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.02)' }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                {t('sentiment.breadth.title')}
              </Typography>
            </Box>
            {rows.filter(r => r.type === 'breadth').map((row, idx, arr) => (
              <Box key={row.metric} sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                px: 2, py: 1,
                borderBottom: idx < arr.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
              }}>
                <Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{row.metric}</Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', mt: 0.2 }}>{row.ref}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, fontFamily: 'monospace', color: row.color }}>
                    {row.value}
                  </Typography>
                  <Box sx={{ color: row.color, display: 'flex' }}>{row.icon}</Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Row 4 — Chart
   ═══════════════════════════════════════════════════════════════ */

// Fetch VNINDEX close prices covering the date range of a given signal history
// (used both by the MSI chart and the Market Psychology chart to overlay price).
const fetchVnindexPriceMap = async (hist) => {
  if (!hist?.length) return {};
  const tradeDates = [...new Set(
    hist.map((row) => String(row.trade_date).slice(0, 10)),
  )].sort((a, b) => a.localeCompare(b));
  const startDate = tradeDates[0];
  const endDate = tradeDates.at(-1);
  const tradeDateSet = new Set(tradeDates);

  try {
    const correlation = await behaviorStatsAPI.getVNIndexCorrelation(
      startDate,
      endDate,
      Math.max(120, tradeDates.length + 30),
      10,
    );
    return Object.fromEntries(
      (correlation?.data_points || [])
        .filter((point) => tradeDateSet.has(String(point.trade_date).slice(0, 10)))
        .map((point) => [String(point.trade_date).slice(0, 10), point.vnindex_close]),
    );
  } catch (err) {
    console.warn('Failed to load VNINDEX prices:', err);
    return {};
  }
};

const getVnindexDomain = (points) => {
  const values = points
    .map((point) => point.vnindex)
    .filter((value) => value != null);
  if (!values.length) return ['auto', 'auto'];

  const low = Math.min(...values);
  const high = Math.max(...values);
  const padding = Math.max((high - low) * 0.06, 20);
  return [Math.floor(low - padding), Math.ceil(high + padding)];
};

const formatChartTooltipValue = (entry) => {
  if (entry.dataKey === 'vnindex') return `${Number(entry.value).toFixed(2)} ₫`;
  const suffix = entry.dataKey === 'msi' ? '' : '%';
  return `${Number(entry.value).toFixed(1)}${suffix}`;
};

const ChartTooltip = ({ active, payload, label, lineMeta }) => {
  if (!active || !payload?.length) return null;
  const date = new Date(label);
  return (
    <div style={{
      background: '#fff', border: '1px solid #ccc', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      fontSize: 12, lineHeight: 1.8,
    }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4, color: '#333' }}>
        {`${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`}
      </div>
      {payload.map((entry) => {
        if (entry.value == null) return null;
        const meta = lineMeta[entry.dataKey] || { color: entry.color, label: entry.dataKey };
        const formatted = formatChartTooltipValue(entry);
        return (
          <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, display: 'inline-block' }} />
            <span style={{ color: '#666' }}>{meta.label}:</span>
            <span style={{ fontWeight: 700, color: meta.color }}>{formatted}</span>
          </div>
        );
      })}
    </div>
  );
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    dataKey: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
  })),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lineMeta: PropTypes.objectOf(PropTypes.shape({
    color: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
};

ChartTooltip.defaultProps = {
  active: false,
  payload: [],
  label: null,
};

const ChartCard = ({ history, vnindexByDate = {}, t }) => {
  const chartData = useMemo(() => (
    [...history].reverse().map((row) => {
      const tradeDate = String(row.trade_date).slice(0, 10);
      return {
        date: tradeDate,
        msi: row.msi,
        vnindex: vnindexByDate[tradeDate] ?? null,
        exposure: (row.exposure_target ?? 0) * 100,
        hedge: (row.hedge_ratio ?? 0) * 100,
      };
    })
  ), [history, vnindexByDate]);

  const vnindexDomain = useMemo(() => getVnindexDomain(chartData), [chartData]);

  const exportRef = useRef(null);
  const [exportingImage, setExportingImage] = useState(false);
  const handleExportImage = async () => {
    if (!exportRef.current || exportingImage) return;
    try {
      setExportingImage(true);
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const asOfDate = history?.[0]?.trade_date || new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `vnindex-msi-history-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingImage(false);
    }
  };

  if (!history?.length) return null;

  const lineMeta = {
    vnindex: { color: '#546e7a', label: t('sentiment.chart.legendVnindex', 'VNINDEX'), axis: 'left' },
    msi: { color: '#1976d2', label: t('sentiment.chart.legendMsi', 'MSI'), axis: 'right', dashed: false },
    exposure: { color: '#43a047', label: t('sentiment.chart.legendExposure', 'Exposure %'), axis: 'right', dashed: true },
    hedge: { color: '#ff6d00', label: t('sentiment.chart.legendHedge', 'Hedge %'), axis: 'right', dashed: true },
  };

  return (
    <Card ref={exportRef} sx={{ width: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                {t('sentiment.history.title')} &mdash; 30 {t('sentiment.history.days')}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}>
                {t('sentiment.history.subtitle', 'MSI vs VNINDEX price correlation')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {Object.entries(lineMeta).map(([key, meta]) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      width: 16,
                      height: 3,
                      bgcolor: meta.color,
                      borderRadius: 1,
                      opacity: meta.dashed ? 0.85 : 1,
                      ...(meta.dashed ? { backgroundImage: `repeating-linear-gradient(90deg, ${meta.color} 0 4px, transparent 4px 7px)` } : {}),
                    }} />
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{meta.label}</Typography>
                  </Box>
                ))}
              </Box>
              <Tooltip title={exportingImage ? t('sentiment.history.exportingImage', 'Exporting image...') : t('sentiment.history.exportImage', 'Export image')}>
                <span>
                  <IconButton size="small" onClick={handleExportImage} disabled={exportingImage} data-html2canvas-ignore="true">
                    {exportingImage ? <CircularProgress size={16} /> : <FileDownloadOutlined fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#999' }}
                angle={-45}
                textAnchor="end"
                height={55}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getDate()}/${date.getMonth() + 1}`;
                }}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{ fontSize: 10, fill: '#999' }}
                domain={vnindexDomain}
                tickFormatter={(value) => `${(value / 1000).toFixed(1)}k`}
                label={{ value: 'VNINDEX', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#777' } }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10, fill: '#999' }}
                domain={[0, 100]}
                label={{ value: 'MSI / %', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#777' } }}
              />
              <RTooltip
                position={{ y: -10 }}
                wrapperStyle={{ zIndex: 1000, outline: 'none' }}
                content={<ChartTooltip lineMeta={lineMeta} />}
              />
              <Legend verticalAlign="top" height={0} />
              {[20, 35, 55, 70, 85].map((level) => (
                <ReferenceLine
                  key={level}
                  yAxisId="right"
                  y={level}
                  stroke="#ddd"
                  strokeDasharray="4 3"
                  strokeWidth={0.7}
                />
              ))}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="vnindex"
                name={lineMeta.vnindex.label}
                stroke={lineMeta.vnindex.color}
                strokeWidth={2.5}
                dot={false}
                connectNulls
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="msi"
                name={lineMeta.msi.label}
                stroke={lineMeta.msi.color}
                strokeWidth={2.5}
                dot={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="exposure"
                name={lineMeta.exposure.label}
                stroke={lineMeta.exposure.color}
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="5 3"
                opacity={0.75}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="hedge"
                name={lineMeta.hedge.label}
                stroke={lineMeta.hedge.color}
                strokeWidth={1.5}
                dot={false}
                strokeDasharray="5 3"
                opacity={0.75}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Main
   ═══════════════════════════════════════════════════════════════ */

const MarketSentiment = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [vnindexByDate, setVnindexByDate] = useState({});
  const [stats, setStats] = useState(null);
  const [calculating, setCalculating] = useState(false);
  
  // Behavior Statistics state
  const [behaviorSignal, setBehaviorSignal] = useState(null);
  const [behaviorInsights, setBehaviorInsights] = useState(null);
  const [eventStats, setEventStats] = useState([]);
  const [behaviorLoading, setBehaviorLoading] = useState(true);
  const [behaviorStatsCalculating, setBehaviorStatsCalculating] = useState(false);
  const [vnindexChartRefreshKey, setVnindexChartRefreshKey] = useState(0);
  const behaviorAutoCalcAttempted = useRef(false);

  // Market Psychology (experimental, VNINDEX) state
  const psychologyIndexCode = 'VNINDEX';
  const [psychologySignal, setPsychologySignal] = useState(null);
  const [psychologyStats, setPsychologyStats] = useState(null);
  const [psychologyHistory, setPsychologyHistory] = useState([]);
  const [psychologyVnindexByDate, setPsychologyVnindexByDate] = useState({});
  const [psychologyHistoryDays, setPsychologyHistoryDays] = useState(180); // matches MarketPsychologySection's default "6M" range option
  const [psychologyLoading, setPsychologyLoading] = useState(true);
  const [psychologyRefreshing, setPsychologyRefreshing] = useState(false);
  const [psychologyCalculating, setPsychologyCalculating] = useState(false);
  const psychologyAutoCalcAttempted = useRef(false);
  const psychologyHasLoadedRef = useRef(false);

  const isToday = useCallback((dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return dateStr === `${yyyy}-${mm}-${dd}`;
  }, []);

  const loadVnindexForHistory = useCallback(async (hist) => {
    if (!hist?.length) {
      setVnindexByDate({});
      return;
    }

    const tradeDates = [...new Set(
      hist.map((row) => String(row.trade_date).slice(0, 10)),
    )].sort((a, b) => a.localeCompare(b));
    const startDate = tradeDates[0];
    const endDate = tradeDates.at(-1);
    const tradeDateSet = new Set(tradeDates);

    try {
      const correlation = await behaviorStatsAPI.getVNIndexCorrelation(
        startDate,
        endDate,
        // Request enough points to cover every trading day in range (no sampling gaps).
        Math.max(120, tradeDates.length + 30),
        10,
      );
      const priceMap = Object.fromEntries(
        (correlation?.data_points || [])
          .filter((point) => tradeDateSet.has(String(point.trade_date).slice(0, 10)))
          .map((point) => [String(point.trade_date).slice(0, 10), point.vnindex_close]),
      );
      setVnindexByDate(priceMap);
    } catch (err) {
      console.warn('Failed to load VNINDEX prices for MSI chart:', err);
      setVnindexByDate({});
    }
  }, []);

  const refreshData = useCallback(async () => {
    setError(null);
    try {
      const [lat, hist, st] = await Promise.all([
        sentimentAPI.getLatest(),
        sentimentAPI.getHistory({ limit: 30 }),
        sentimentAPI.getStats({ days: 30 }),
      ]);
      setData(lat);
      setHistory(hist);
      setStats(st);
      await loadVnindexForHistory(hist);
    } catch (err) {
      console.error('Error refreshing sentiment:', err);
      setError(err.message || 'Failed to load');
    }
  }, [loadVnindexForHistory]);

  const calculateAndUpdate = useCallback(async () => {
    setCalculating(true); setError(null);
    try {
      await sentimentAPI.calculate({});
      await refreshData();
    } catch (err) {
      console.error('Calculate error:', err);
      setError(err.message || 'Failed to calculate');
    } finally { setCalculating(false); }
  }, [refreshData]);

  // Fetch behavior statistics (panic/fomo scores, insights, similar days, events)
  const fetchBehaviorStats = useCallback(async (sentimentTradeDate) => {
    try {
      setBehaviorLoading(true);
      const signal = await behaviorStatsAPI.getLatestSignal();
      const referenceDate = sentimentTradeDate
        ? String(sentimentTradeDate).slice(0, 10)
        : null;
      const behaviorDate = signal?.trade_date
        ? String(signal.trade_date).slice(0, 10)
        : null;
      if (
        referenceDate
        && behaviorDate
        && behaviorDate < referenceDate
        && !behaviorAutoCalcAttempted.current
      ) {
        behaviorAutoCalcAttempted.current = true;
        setBehaviorStatsCalculating(true);
        try {
          await behaviorStatsAPI.calculate({
            start_date: behaviorDate,
            end_date: referenceDate,
          });
          setVnindexChartRefreshKey((key) => key + 1);
          return await fetchBehaviorStats(referenceDate);
        } finally {
          setBehaviorStatsCalculating(false);
        }
      }

      const insights = await behaviorStatsAPI.getMarketInsights();

      setBehaviorSignal(signal);
      setBehaviorInsights(insights);

      const eventNames = getRelevantEventNames(signal);
      if (eventNames.length === 0) {
        setEventStats([]);
        return;
      }

      const eventResults = await Promise.allSettled(
        eventNames.map((name) => behaviorStatsAPI.getEventStatistics(name, 20)),
      );

      const events = eventResults
        .filter((result) => result.status === 'fulfilled' && result.value?.is_valid)
        .map((result) => result.value);

      setEventStats(events);
    } catch (err) {
      console.warn('Error fetching behavior stats:', err);
    } finally {
      setBehaviorLoading(false);
    }
  }, []);

  const calculateBehaviorStats = useCallback(async () => {
    behaviorAutoCalcAttempted.current = false;
    setBehaviorStatsCalculating(true);
    try {
      await behaviorStatsAPI.calculate();
      setVnindexChartRefreshKey((key) => key + 1);
      await fetchBehaviorStats();
    } catch (err) {
      console.warn('Error calculating behavior stats:', err);
    } finally {
      setBehaviorStatsCalculating(false);
    }
  }, [fetchBehaviorStats]);

  const fetchData = useCallback(async () => {
    try {
      behaviorAutoCalcAttempted.current = false;
      setLoading(true); setError(null);
      const [lat, hist, st] = await Promise.all([
        sentimentAPI.getLatest(),
        sentimentAPI.getHistory({ limit: 30 }),
        sentimentAPI.getStats({ days: 30 }),
      ]);
      setData(lat);
      setHistory(hist);
      setStats(st);
      await loadVnindexForHistory(hist);
      setLoading(false);

      // Auto-calculate if data is stale (not from today)
      const latestDate = lat?.trade_date;
      if (!isToday(latestDate)) {
        await calculateAndUpdate();
      }
      
      // Fetch behavior statistics in parallel (non-blocking)
      await fetchBehaviorStats(lat?.trade_date);
    } catch (err) {
      console.error('Error fetching sentiment data:', err);
      setError(err.message || 'Failed to load');
      setLoading(false);
    }
  }, [calculateAndUpdate, isToday, fetchBehaviorStats, loadVnindexForHistory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch Market Psychology signal/history/stats for VNINDEX (non-blocking, best-effort)
  const fetchPsychology = useCallback(async () => {
    // Only the very first load blocks the whole section; later refetches (e.g. range dropdown) keep prior data mounted.
    const isInitialLoad = !psychologyHasLoadedRef.current;
    try {
      if (isInitialLoad) setPsychologyLoading(true); else setPsychologyRefreshing(true);
      const [signal, hist, st] = await Promise.all([
        psychologyAPI.getLatest(psychologyIndexCode).catch(() => null),
        psychologyAPI.getHistory({ index_code: psychologyIndexCode, limit: psychologyHistoryDays }).catch(() => []),
        psychologyAPI.getStats({ index_code: psychologyIndexCode, days: psychologyHistoryDays }).catch(() => null),
      ]);
      setPsychologySignal(signal);
      setPsychologyHistory(hist || []);
      setPsychologyStats(st);
      setPsychologyVnindexByDate(await fetchVnindexPriceMap(hist));
    } catch (err) {
      console.warn('Error fetching psychology stats:', err);
    } finally {
      psychologyHasLoadedRef.current = true;
      if (isInitialLoad) setPsychologyLoading(false); else setPsychologyRefreshing(false);
    }
  }, [psychologyHistoryDays]);

  const calculatePsychology = useCallback(async () => {
    setPsychologyCalculating(true);
    try {
      await psychologyAPI.calculate({ index_code: psychologyIndexCode });
      await fetchPsychology();
    } catch (err) {
      console.warn('Error calculating psychology signals:', err);
    } finally {
      setPsychologyCalculating(false);
    }
  }, [fetchPsychology]);

  useEffect(() => {
    (async () => {
      await fetchPsychology();
    })();
  }, [fetchPsychology]);

  // Auto-backfill once if this index has no psychology data yet (never retries after one attempt)
  useEffect(() => {
    if (!psychologyLoading && !psychologySignal && !psychologyCalculating && !psychologyAutoCalcAttempted.current) {
      psychologyAutoCalcAttempted.current = true;
      calculatePsychology();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psychologyLoading, psychologySignal]);

  if (loading) return (
    <Box sx={{ p: 3 }}>
      {/* Header skeleton */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={42} height={42} animation="wave" sx={{ borderRadius: 1.5 }} />
          <Box>
            <Skeleton width={220} height={26} animation="wave" sx={{ borderRadius: 1 }} />
            <Skeleton width={160} height={16} animation="wave" sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={110} height={36} animation="wave" sx={{ borderRadius: '4px' }} />
          <Skeleton variant="rounded" width={90} height={36} animation="wave" sx={{ borderRadius: '4px' }} />
        </Box>
      </Box>

      {/* Row 1 — 4 hero stat card skeletons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          'linear-gradient(135deg, #b0bec5 0%, #cfd8dc 100%)',
          'linear-gradient(135deg, #bcaaa4 0%, #d7ccc8 100%)',
          'linear-gradient(135deg, #90a4ae 0%, #b0bec5 100%)',
          'linear-gradient(135deg, #a5d6a7 0%, #c8e6c9 100%)',
        ].map((bg, i) => (
          <Box key={i} sx={{ flex: '1 1 0', minWidth: 200 }}>
            <Card sx={{ height: '100%', background: bg }}>
              <CardContent>
                <Skeleton width={120} height={14} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 1, mb: 0.5 }} />
                <Skeleton width="80%" height={30} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.35)', borderRadius: 1, mb: 0.5 }} />
                <Skeleton width="90%" height={14} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 1, mb: 1.5 }} />
                <Skeleton variant="rounded" width="100%" height={6} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 3, mb: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Skeleton width={30} height={12} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Skeleton width={80} height={16} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                  <Skeleton width={30} height={12} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Row 2 — Fear Components + Positioning */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Fear Components skeleton */}
        <Box sx={{ flex: '2 1 0', minWidth: 400 }}>
          <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Skeleton width={200} height={20} animation="wave" />
              </Box>
              {[28, 22, 18, 22, 10].map((_, i) => (
                <Box key={i} sx={{ px: 2, py: 1.25, borderBottom: i < 4 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Skeleton width={100 + i * 10} height={16} animation="wave" />
                      <Skeleton variant="rounded" width={32} height={16} animation="wave" sx={{ borderRadius: 8 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Skeleton width={50} height={14} animation="wave" />
                      <Skeleton width={55} height={16} animation="wave" />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="rounded" width="100%" height={10} animation="wave" sx={{ borderRadius: 1 }} />
                    <Skeleton width={80} height={12} animation="wave" sx={{ flexShrink: 0 }} />
                  </Box>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderTop: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Skeleton width={80} height={18} animation="wave" />
                <Skeleton width={60} height={20} animation="wave" />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Positioning skeleton */}
        <Box sx={{ flex: '1 1 0', minWidth: 300 }}>
          <Card sx={{ height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                <Skeleton width={180} height={20} animation="wave" />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, flex: 1 }}>
                {[0, 1, 2, 3].map(i => (
                  <Box key={i} sx={{
                    px: 2, py: 1.25, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                    borderRight: i % 2 === 0 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <Skeleton variant="circular" width={18} height={18} animation="wave" />
                      <Skeleton width={70} height={14} animation="wave" />
                    </Box>
                    <Skeleton width={80} height={32} animation="wave" />
                    <Skeleton width={100} height={12} animation="wave" sx={{ mt: 0.25 }} />
                  </Box>
                ))}
              </Box>
              <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.015)' }}>
                <Skeleton width="100%" height={14} animation="wave" />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Row 3 — Market Data table skeleton */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton variant="circular" width={20} height={20} animation="wave" />
              <Skeleton width={280} height={20} animation="wave" />
            </Box>
            <Box sx={{ display: 'flex' }}>
              {/* Left column */}
              <Box sx={{ flex: 1, borderRight: '1px solid rgba(0,0,0,0.06)' }}>
                <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Skeleton width={80} height={14} animation="wave" />
                </Box>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <Box key={i} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 2, py: 1, borderBottom: i < 5 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                  }}>
                    <Box>
                      <Skeleton width={120 + i * 5} height={16} animation="wave" />
                      <Skeleton width={80} height={12} animation="wave" sx={{ mt: 0.2 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Skeleton width={60} height={18} animation="wave" />
                      <Skeleton variant="circular" width={16} height={16} animation="wave" />
                    </Box>
                  </Box>
                ))}
              </Box>
              {/* Right column */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Skeleton width={80} height={14} animation="wave" />
                </Box>
                {[0, 1, 2, 3, 4].map(i => (
                  <Box key={i} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 2, py: 1, borderBottom: i < 4 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                  }}>
                    <Box>
                      <Skeleton width={110 + i * 8} height={16} animation="wave" />
                      <Skeleton width={75} height={12} animation="wave" sx={{ mt: 0.2 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Skeleton width={55} height={18} animation="wave" />
                      <Skeleton variant="circular" width={16} height={16} animation="wave" />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Row 4 — Chart skeleton */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton width={220} height={20} animation="wave" />
              <Box sx={{ display: 'flex', gap: 2 }}>
                {[0, 1, 2].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Skeleton variant="rounded" width={16} height={3} animation="wave" />
                    <Skeleton width={60} height={14} animation="wave" />
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rounded" width="100%" height={300} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Stats panel skeleton */}
      <Card sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={18} height={18} animation="wave" />
            <Skeleton width={240} height={20} animation="wave" />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
              <Box key={i} sx={{
                textAlign: 'center', py: 1.5, px: 1,
                borderRight: i < 6 ? '1px solid rgba(0,0,0,0.06)' : 'none',
              }}>
                <Skeleton width="70%" height={12} animation="wave" sx={{ mx: 'auto', mb: 0.5 }} />
                <Skeleton width="50%" height={22} animation="wave" sx={{ mx: 'auto' }} />
              </Box>
            ))}
          </Box>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Skeleton width={140} height={14} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="rounded" width="100%" height={22} animation="wave" sx={{ borderRadius: 1.5, mb: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {[0, 1, 2, 3, 4].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Skeleton variant="rounded" width={10} height={10} animation="wave" sx={{ borderRadius: 0.5 }} />
                  <Skeleton width={60 + i * 5} height={14} animation="wave" />
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>{t('common.retry')}</Button>
    </Box>
  );

  return (
    <Box sx={{
      maxWidth: 1500,
      mx: 'auto',
      minHeight: '100vh',
    }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER — Compact Professional Style
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(102,57,234,0.25)',
          }}>
            <ShowChart sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{
                fontWeight: 700,
                color: '#0f172a',
                fontSize: '1.2rem',
                letterSpacing: '-0.01em',
              }}>
                {t('sentiment.title')}
              </Typography>
              {data?.trade_date && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.6,
                  bgcolor: '#f1f5f9',
                  px: 1,
                  py: 0.3,
                  borderRadius: 1.5,
                }}>
                  <Box sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#667eea',
                    boxShadow: '0 0 6px #667eea',
                  }} />
                  <Typography sx={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#475569',
                  }}>
                    {data.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('sentiment.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Right: Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <Calculate />}
            onClick={calculateAndUpdate}
            disabled={calculating}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#667eea', '&:hover': { bgcolor: '#5568d3' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {calculating ? 'Đang tính...' : 'Tính ngay'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refreshData()}
            disabled={loading}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Calculating Banner */}
      {calculating && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }} icon={<CircularProgress size={18} />}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Đang tính toán sentiment với dữ liệu mới nhất...</Typography>
        </Alert>
      )}

      {/* Row 1 — Hero stats */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 1.5,
        mb: 2.5,
      }}>
        <MSIScoreCard data={data} prev={history.length > 1 ? history[1] : null} t={t} />
        <FearScoreCard data={data} prev={history.length > 1 ? history[1] : null} t={t} />
        <StressCard data={data} prev={history.length > 1 ? history[1] : null} t={t} />
        <ExposureCard data={data} prev={history.length > 1 ? history[1] : null} t={t} />
      </Box>

      {/* Trader Plan strip — alert/urgency/conviction/hedge/invalidation */}
      <Box sx={{ mb: 2.5 }}>
        <TraderPlanStrip data={data} t={t} />
      </Box>

      {/* Row 2 — Fear + Positioning */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
        gap: 2,
        mb: 2.5,
      }}>
        <FearComponentsCard data={data} t={t} />
        <PositioningPanel data={data} t={t} />
      </Box>

      {/* Row 3 — Market Data */}
      <Box sx={{ mb: 2.5 }}>
        <MarketDataTable data={data} t={t} />
      </Box>

      {/* Row 4 — Chart */}
      <Box sx={{ mb: 2.5 }}>
        <ChartCard history={history} vnindexByDate={vnindexByDate} t={t} />
      </Box>

      {/* Stats panel */}
      {stats && (() => {
        const stateColorMap = {
          PANIC: '#d32f2f', BOTTOMING: '#e65100', NEUTRAL: '#757575',
          RISK_ON: '#2e7d32', DISTRIBUTION: '#f9a825', EUPHORIA: '#c62828',
        };
        const totalStateDays = (stats.states || []).reduce((s, st) => s + st.count, 0) || 1;
        const kpis = [
          { label: t('sentiment.stats.avgMsi'), value: stats.avg_msi?.toFixed(1), color: '#1976d2' },
          { label: t('sentiment.stats.maxMsi'), value: stats.max_msi?.toFixed(1), color: '#2e7d32' },
          { label: t('sentiment.stats.minMsi'), value: stats.min_msi?.toFixed(1), color: '#d32f2f' },
          { label: t('sentiment.stats.avgFear'), value: stats.avg_fear?.toFixed(2), color: '#e65100' },
          { label: t('sentiment.stats.avgExposure'), value: `${((stats.avg_exposure ?? 0) * 100).toFixed(1)}%`, color: '#43a047' },
          { label: t('sentiment.stats.avgHedge'), value: `${((stats.avg_hedge ?? 0) * 100).toFixed(1)}%`, color: '#ff6d00' },
          { label: t('sentiment.stats.totalDays'), value: stats.total_days, color: '#546e7a' },
        ];
        return (
          <Card sx={{
            width: '100%',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            border: '1px solid rgba(0,0,0,0.06)',
            mb: 2.5,
          }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {/* Header */}
              <Box sx={{
                px: 1.5,
                py: 1,
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                bgcolor: 'rgba(0,0,0,0.02)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <ShowChart sx={{ fontSize: '1rem' }} />
                <Typography sx={{
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: '#475569',
                }}>
                  {t('sentiment.stats.title')} — {t('sentiment.stats.nDays', { n: 30 })}
                </Typography>
              </Box>

              {/* KPI grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(100px, 1fr))', md: 'repeat(7, 1fr)' },
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}>
                {kpis.map((kpi, i) => (
                  <Box key={kpi.label} sx={{
                    textAlign: 'center',
                    py: 1,
                    px: 1,
                    borderRight: i < kpis.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.015)' },
                  }}>
                    <Typography sx={{
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      color: 'text.disabled',
                      textTransform: 'uppercase',
                      letterSpacing: 0.3,
                      mb: 0.4,
                    }}>
                      {kpi.label}
                    </Typography>
                    <Typography sx={{
                      fontSize: '1rem',
                      fontWeight: 800,
                      fontFamily: 'monospace',
                      color: kpi.color,
                    }}>
                      {kpi.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* State distribution bar */}
              {stats.states?.length > 0 && (
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', mb: 1 }}>
                    {t('sentiment.stats.stateDistribution')}
                  </Typography>
                  {/* Stacked bar */}
                  <Box sx={{ display: 'flex', height: 22, borderRadius: 1.5, overflow: 'hidden', mb: 1 }}>
                    {stats.states.map((st, i) => (
                      <Box key={st.market_state} sx={{
                        width: `${(st.count / totalStateDays) * 100}%`,
                        bgcolor: stateColorMap[st.market_state] || '#999',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'width 0.3s ease',
                        minWidth: st.count > 0 ? 24 : 0,
                      }}>
                        <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                          {st.count}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {/* Legend */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {stats.states.map((st, i) => (
                      <Box key={st.market_state} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: stateColorMap[st.market_state] || '#999' }} />
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                          {t(`sentiment.stateLabels.${st.market_state?.toLowerCase()}`) || st.market_state}
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
                          {t('sentiment.stats.daysCount', { count: st.count })} ({((st.count / totalStateDays) * 100).toFixed(0)}%)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Behavior Statistics Section */}
      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            mb: 1.25,
            color: 'text.secondary',
          }}
        >
          {t('sentiment.behavior.section_title', 'Market Behavior Statistics')}
        </Typography>

        {behaviorLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (behaviorSignal || behaviorInsights) && (
          <Box sx={{ mb: 2 }}>
            <BehaviorOverview signal={behaviorSignal} insights={behaviorInsights} t={t} />
          </Box>
        )}

        {/* Analytics - Full Width Charts */}
        {/* Similar Days Visualization */}
        {!behaviorLoading && behaviorSignal?.trade_date && (
          <Box sx={{ mb: 2.5 }}>
            <SimilarDaysVisualization referenceDate={behaviorSignal.trade_date} t={t} />
          </Box>
        )}

        {/* VNINDEX Correlation Chart */}
        {!behaviorLoading && (
          <Box sx={{ mb: 2.5 }}>
            <VNIndexCorrelationChart
              refreshTrigger={vnindexChartRefreshKey}
              calculating={behaviorStatsCalculating}
              onCalculate={calculateBehaviorStats}
              t={t}
            />
          </Box>
        )}

        {/* Event Performance Table - Full Width */}
        {!behaviorLoading && (
          <Box sx={{ mb: 2.5 }}>
            <EventPerformanceTable
              events={eventStats}
              loading={behaviorLoading}
              signal={behaviorSignal}
              t={t}
            />
          </Box>
        )}
      </Box>

      {/* Market Psychology Section (experimental, VNINDEX) */}
      <Box sx={{ mt: 3 }}>
        <MarketPsychologySection
          indexCode={psychologyIndexCode}
          signal={psychologySignal}
          stats={psychologyStats}
          history={psychologyHistory}
          vnindexByDate={psychologyVnindexByDate}
          historyDays={psychologyHistoryDays}
          onHistoryDaysChange={setPsychologyHistoryDays}
          loading={psychologyLoading}
          refreshing={psychologyRefreshing}
          calculating={psychologyCalculating}
          onCalculate={calculatePsychology}
          t={t}
        />
      </Box>
    </Box>
  );
};

export default MarketSentiment;
